import React, { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useProductTour } from 'components/onboarding'
import { useReferrerState } from 'hooks'
import { ROUTES } from 'routes'
import type { IAnnouncement } from 'services/announcementsApi'

/**
 * What an announcement's CTA DOES, and where the reader came from — one implementation,
 * for both surfaces that render the button.
 *
 * Extracted (§36) when the modal moved its CTA out of the card body and into the dialog
 * FOOTER. That is a chrome move, not a content one — the card still owns whether a CTA
 * exists and what it says; only the frame it is painted in changed. But it does mean two
 * components now render a CTA button, and the FIRST hand-roll is allowed while the second
 * is a shared unit. The behaviour underneath is the fiddly part and exactly the kind that
 * drifts when copied: the navigate-then-tour ordering below is a real bug when reversed,
 * and it would be invisible in review.
 *
 * Returns the referrer as well, so the card's other outbound links (the detail page, the
 * "you asked for this" request links) derive it from the same call rather than a second
 * one that could be given a different label.
 */
/**
 * `announcement` is NULLABLE on purpose. The modal calls this before its
 * `if (!candidate) return null`, because a hook cannot sit after an early return — so
 * the alternative is not "pass a non-null value", it is "duplicate the handler back
 * into the modal", which is the drift this extraction exists to prevent. With nothing
 * to announce, `hasCta` is false and `onCta` is inert.
 */
export const useAnnouncementCta = (
  announcement: IAnnouncement | null | undefined,
  onActioned?: () => void,
) => {
  const navigate = useNavigate()
  // `startTour`, not `startTourOnce`: this is an explicit CTA. A user clicking "Show
  // me" after already having seen that tour still means "show me", and
  // `startTourOnce` would silently do nothing for them.
  const { startTour } = useProductTour()

  /*
    Handed to every navigation out of an announcement, so the destination offers a way
    back to where the reader actually came from (§42).

    The label is overridden on the WHAT'S-NEW routes only. `useReferrerState` derives
    its label by title-casing the last path segment that is not an id, which turns both
    `/whats-new` and `/whats-new/<uuid>` into "Whats New" — wrong capitalisation and a
    missing apostrophe, printed straight into "← Back to Whats New". It cannot be
    hard-coded, though: the same card renders inside the modal, which floats over
    whatever page the reader was on, and there the derived label ("Certificates",
    "Products") is exactly right. So override on the routes whose slug does not
    title-case cleanly, and leave the derivation alone everywhere else.

    `startsWith` covers the detail route as well as the archive, which matters because
    the CTA fires from BOTH: a reader who opens an announcement at its own address and
    then clicks "Show me" should get "← Back to What's new", not "Whats New", and not a
    link back to a page that names one announcement they have already read. `backTo`
    still points at wherever they actually were — the label is the only derived part
    being corrected. The equivalent fix for the BREADCRUMB lives in `Breadcrumbs`'
    `SEGMENT_LABELS`, because that component derives its own labels from the path and
    never sees this value.
  */
  const { pathname } = useLocation()
  const referrer = useReferrerState(
    pathname.startsWith(ROUTES.WHATS_NEW) ? "What's new" : undefined,
  )

  const ctaLabel = announcement?.ctaLabel
  const ctaRoute = announcement?.ctaRoute
  const tourKey = announcement?.tourKey
  const hasCta = Boolean(ctaLabel && (ctaRoute || tourKey))

  /**
   * The CTA may NAVIGATE, START A TOUR, or both — and when both are set the order
   * matters: navigate FIRST, then start the tour. A tour's steps are anchored to
   * `data-tour` elements on a specific page, so starting it before the route change
   * spotlights anchors that are not mounted yet.
   *
   * `onActioned` fires before either, so the modal is already closing — ToursDialog
   * does the same thing for the same reason: the dialog backdrop swallows the tour's
   * spotlight.
   */
  const onCta = useCallback(
    (event?: React.MouseEvent) => {
      // In the FEED the whole card navigates to the detail page. Without this, a CTA
      // click fires BOTH — the card's handler and this one — and the reader lands
      // wherever the second navigation happened to win. Harmless from the modal
      // footer, which has no clickable ancestor, so it is unconditional rather than
      // a per-surface flag.
      event?.stopPropagation()
      onActioned?.()
      // `state: referrer` so the page the CTA lands on offers "← Back to What's new"
      // (§42). Arriving via an announcement is exactly the interrupted-task navigation
      // that rule exists for: the reader was reading the changelog, not browsing to
      // certificates, and without it the only way back is the sidebar.
      if (ctaRoute) navigate(ctaRoute, { state: referrer })
      if (tourKey) startTour(tourKey)
    },
    [ctaRoute, tourKey, navigate, referrer, startTour, onActioned],
  )

  return { hasCta, ctaLabel, referrer, onCta }
}
